import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Header, HeaderTitle } from 'components/Header';
import { CopyValue } from 'components/CopyValue';
import { Loader } from 'components/Loader';
import { ActionsAndSave, PageActions, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { ColumnsSectionContainer, SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { DetailArrayValue } from 'components/DetailsView/DetailArrayValue';
import { DetailValue } from 'components/DetailsView/DetailValue';
import { StateHistory } from 'components/DetailsView/StateHistory';
import { FilterChip, FilterChips } from 'components/FilterChip';
import { PAGES } from 'constants/pages';
import { DeliveryState, TaskState, TourState, useGetTourQuery } from 'data/__generated__';

import { boxCounts, failedDeliveryCount, deliveryLabel, getStopState } from '../helpers';
import { DeliveryCard, deliveryIssues } from './DeliveryCard';
import { StateSelect, useTourStateUpdate } from './StateSelect';
import { TaskCard } from './TaskCard';
import { TourStatusTag } from '../components/TourStatusTag';
import { dueScanSteps, formatHour, isTourBack, type DetailDelivery, type DetailTour } from './helpers';

const DELIVERED_STATES = [DeliveryState.Delivered, DeliveryState.Archived];
const CLOSED_STATES = [...DELIVERED_STATES, DeliveryState.NotDelivered, DeliveryState.Aborted];

/** When the truck left, when the last stop (delivery or task) was closed, and the worst delay of the day. */
const tourTiming = (tour: DetailTour) => {
    const closures = [
        ...tour.deliveries.map((delivery) => delivery.times.actualSlot.end),
        ...tour.stops.flatMap((stop) =>
            stop.__typename === 'Task' && (stop.state === TaskState.Finished || stop.state === TaskState.Failed)
                ? [stop.stateHistory[stop.stateHistory.length - 1].createdAt]
                : [],
        ),
    ].filter((time): time is number => time != null);

    return {
        departedAt: tour.stateHistory.find(({ state }) => state === TourState.InDelivery)?.createdAt ?? null,
        lastClosureAt: closures.length ? Math.max(...closures) : null,
    };
};

/**
 * One tour, audited after the fact: who drove it, what each delivery cost against its estimate, the steps every
 * delivery went through, and the boxes — what left the sas and what came back. The only edit here is the
 * driving state (`StateSelect`), the one thing no other screen can repair; the dispatch actions (rename,
 * assign, re-sequence) stay on `/tours`, where the whole day is visible.
 */
export const TourDetails = () => {
    const { tourId = '' } = useParams<{ tourId: string }>();
    const { search } = useLocation();
    const { data, error } = useGetTourQuery({
        variables: { tourId },
        fetchPolicy: 'cache-and-network',
    });
    const [pendingState, setPendingState] = useState<TourState | null>(null);
    const stateUpdate = useTourStateUpdate({
        tour: data?.tour,
        pendingState,
        onApplied: () => setPendingState(null),
    });

    // `tour` is non-nullable: a stale link answers with a GraphQL error and no data at all — say so
    // instead of spinning. A failed REFRESH (error with data already in hand) keeps showing what we have.
    if (error && !data) {
        return (
            <Container>
                <Shell>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.tourDetails} />
                        </HeaderTitle>
                        <BackAction />
                    </Header>
                    <Missing>Tournée introuvable</Missing>
                </Shell>
            </Container>
        );
    }

    if (!data) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    const { tour } = data;
    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [{ label: 'Retour aux tournées', icon: <ArrowLeft />, to: `/tours/${tour.date}${search}` }],
        },
    ];

    return (
        <Container>
            <Shell>
                <Header>
                    <HeaderTitle>
                        <PageTitle page={{ ...PAGES.tourDetails, label: `Tournée ${tour.name}` }} />
                    </HeaderTitle>
                    <ActionsAndSave
                        actionGroups={actionGroups}
                        onSave={stateUpdate.applyState}
                        saveDisabled={!stateUpdate.isDirty}
                        saveLoading={stateUpdate.loading}
                        saveTitle="Mettre à jour"
                    />
                </Header>
                <Content>
                    <ScrollableContent>
                        {/* Two sections, like the delivery page: the tour as a whole, then its deliveries —
                            all open, nothing to unfold to read the day. */}
                        <GlobalSection tour={tour} pendingState={pendingState} onStateChange={setPendingState} />
                        <DeliveriesSection tour={tour} />
                        <TasksSection tour={tour} />
                    </ScrollableContent>
                </Content>
            </Shell>
        </Container>
    );
};

/** Stale link: no tour, so no day to go back to — today's tours page. */
const BackAction = () => (
    <PageActions>
        <Link to={PAGES.tours.url}>
            <TotemPrimaryButton $isSecondaryStyle>
                <ArrowLeft size={16} /> Retour aux tournées
            </TotemPrimaryButton>
        </Link>
    </PageActions>
);

const GlobalSection = ({
    tour,
    pendingState,
    onStateChange,
}: {
    tour: DetailTour;
    pendingState: TourState | null;
    onStateChange: (state: TourState | null) => void;
}) => {
    const deliveredCount = tour.deliveries.filter((delivery) =>
        DELIVERED_STATES.includes(getStopState(delivery)),
    ).length;
    const failedCount = failedDeliveryCount(tour);
    const tasks = tour.stops.flatMap((stop) => (stop.__typename === 'Task' ? [stop] : []));
    const doneTaskCount = tasks.filter(({ state }) => state === TaskState.Finished).length;
    const { departedAt, lastClosureAt } = tourTiming(tour);

    return (
        <SectionContainer title={`Tournée ${tour.name}`} isInitiallyOpen>
            <ColumnsSectionContainer numberOfColumns={3}>
                <SectionColumn>
                    <CopyValue label="ID de la tournée" value={tour._id} />
                    <TourStatusTag state={tour.state} showHeading />
                    <StateSelect value={pendingState ?? tour.state} onChange={onStateChange} />
                    <DetailValue label="Date" value={tour.date} />
                    <DetailValue
                        label="Appro"
                        value={tour.deliveryPerson?.fullname ?? 'Non affectée'}
                        isValid={tour.deliveryPerson ? undefined : false}
                    />
                    <DetailValue
                        label="Arrêts"
                        sublabel={`${deliveredCount} livré(s) · ${failedCount} non livré(s)${tasks.length ? ` · ${doneTaskCount}/${tasks.length} tâche(s) terminée(s)` : ''}`}
                        value={tour.stops.length}
                    />
                    <DetailValue label="Départ du dépôt" value={formatHour(departedAt)} />
                    <DetailValue label="Dernière clôture" value={formatHour(lastClosureAt)} />
                </SectionColumn>
                <SectionColumn>
                    <BoxesRecap tour={tour} />
                </SectionColumn>
                <SectionColumn>
                    <StateHistory
                        label="Historique de la tournée"
                        records={tour.stateHistory.map((record) => ({
                            ...record,
                            // Each record carries its OWN state — the tag reads the history, not the tour's
                            // current one.
                            state: <TourStatusTag state={record.state} />,
                        }))}
                    />
                </SectionColumn>
            </ColumnsSectionContainer>
        </SectionContainer>
    );
};

/**
 * The tour's boxes seen as one set: the sas scan on departure, the return check at the warehouse. A box
 * still at `returnedAt: null` once the tour is back is a missing box — this table is where ops sees it.
 */
const BoxesRecap = ({ tour }: { tour: DetailTour }) => {
    const { total, loaded, returned } = boxCounts(tour);
    const hasDeparted = tour.deliveries.some((delivery) => dueScanSteps(tour, delivery).loadedAt);
    const isBack = isTourBack(tour);

    // The boxes worth naming: never scanned out once the truck has left, or never scanned back once it is
    // home. Before those moments there is nothing to chase — an empty date is the normal state of things.
    const toChase = tour.deliveries.flatMap((delivery) =>
        delivery.boxes
            .filter((box) => (hasDeparted && box.loadedAt === null) || (isBack && box.returnedAt === null))
            .map((box) =>
                box.loadedAt === null
                    ? `#${box.number} ${box.deliveryCode} — ${deliveryLabel(delivery)}, départ non scanné`
                    : `#${box.number} ${box.deliveryCode} — ${deliveryLabel(delivery)}, retour non scanné`,
            ),
    );

    return (
        <>
            <DetailValue label="Caisses de la tournée" value={total} />
            <DetailValue
                label="Départ scanné"
                value={`${loaded}/${total}`}
                isValid={total && hasDeparted ? loaded === total : undefined}
            />
            <DetailValue
                label="Retour scanné"
                value={isBack || returned ? `${returned}/${total}` : 'Retour non commencé'}
                isValid={total && isBack ? returned === total : undefined}
            />
            {toChase.length ? <DetailArrayValue label="Caisses à retrouver" values={toChase} /> : null}
        </>
    );
};

type DeliveryFilter = 'all' | 'issues' | 'delivered' | 'pending';

const DeliveriesSection = ({ tour }: { tour: DetailTour }) => {
    const [filter, setFilter] = useState<DeliveryFilter>('all');

    const counts = {
        all: tour.deliveries.length,
        issues: tour.deliveries.filter((delivery) => deliveryIssues(delivery, tour).length > 0).length,
        delivered: tour.deliveries.filter((delivery) => DELIVERED_STATES.includes(getStopState(delivery))).length,
        pending: tour.deliveries.filter((delivery) => !CLOSED_STATES.includes(delivery.state)).length,
    };

    const matches = (delivery: DetailDelivery): boolean => {
        if (filter === 'issues') return deliveryIssues(delivery, tour).length > 0;
        if (filter === 'delivered') return DELIVERED_STATES.includes(getStopState(delivery));
        if (filter === 'pending') return !CLOSED_STATES.includes(delivery.state);
        return true;
    };

    // The sequence is the tour's visit order (tasks included), never the filtered list's: « 7 » must stay the 7th
    // stop of the day — the number totops shows.
    const sequenceById = new Map(tour.stops.map(({ _id }, index) => [_id, index + 1]));
    const deliveries = tour.deliveries
        .map((delivery) => ({ delivery, sequence: sequenceById.get(delivery._id) ?? 0 }))
        .filter(({ delivery }) => matches(delivery));

    return (
        <SectionContainer title={`Livraisons (${tour.deliveries.length})`} isInitiallyOpen>
            <>
                <FilterChips>
                    {FILTERS.map(({ value, label }) => (
                        <FilterChip
                            key={value}
                            type="button"
                            $isActive={filter === value}
                            $isAlert={value === 'issues' && counts.issues > 0}
                            onClick={() => setFilter(value)}
                        >
                            {label} ({counts[value]})
                        </FilterChip>
                    ))}
                </FilterChips>
                <Deliveries>
                    {deliveries.map(({ delivery, sequence }) => (
                        <DeliveryCard key={delivery._id} delivery={delivery} sequence={sequence} tour={tour} />
                    ))}
                    {deliveries.length === 0 ? (
                        <Missing>
                            {tour.deliveries.length
                                ? 'Aucune livraison dans ce filtre'
                                : 'Cette tournée n’a aucune livraison'}
                        </Missing>
                    ) : null}
                </Deliveries>
            </>
        </SectionContainer>
    );
};

const TasksSection = ({ tour }: { tour: DetailTour }) => {
    const tasks = tour.stops.flatMap((stop, index) =>
        stop.__typename === 'Task' ? [{ task: stop, sequence: index + 1 }] : [],
    );
    if (!tasks.length) return null;

    return (
        <SectionContainer title={`Tâches (${tasks.length})`} isInitiallyOpen>
            <Deliveries>
                {tasks.map(({ task, sequence }) => (
                    <TaskCard key={task._id} task={task} sequence={sequence} />
                ))}
            </Deliveries>
        </SectionContainer>
    );
};

const FILTERS: { value: DeliveryFilter; label: string }[] = [
    { value: 'all', label: 'Toutes' },
    { value: 'issues', label: 'À contrôler' },
    { value: 'delivered', label: 'Livrées' },
    { value: 'pending', label: 'En cours' },
];

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Shell = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const Missing = styled.div`
    padding: 30px;
    text-align: center;
    color: ${({ theme }) => theme.infoTextColor};
`;

// A hairline between deliveries, like every list in the back-office — no card, no coloured bar.
const Deliveries = styled.div`
    display: flex;
    flex-direction: column;

    & > :not(:first-child) {
        border-top: 1px solid ${({ theme }) => theme.lightBorderColor};
    }
`;
